/**
 * Home Moments data (Sep 2026): one `hubMoments` call per landing, cached in
 * memory for a few minutes so hopping Home ↔ an app doesn't re-fetch (the
 * callable can cold-start). A nomination marks the cache stale so the Drivers
 * card flips to "thanks" on the next landing.
 */
import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { CALLABLES, type HubClapInput, type HubClapResponse, type HubMomentsResponse } from '@daaily/shared';

const callHubMoments = httpsCallable<void, HubMomentsResponse>(functions, CALLABLES.HUB_MOMENTS);
export const callHubClap = httpsCallable<HubClapInput, HubClapResponse>(functions, CALLABLES.HUB_CLAP);

const TTL_MS = 5 * 60 * 1000;
let cache: { email: string; at: number; data: HubMomentsResponse } | null = null;

/** Call after the caller nominates, so the pulse reloads on the next Home visit. */
export function invalidateMoments() { cache = null; }

/** Keep the cache in step with an optimistic clap. */
export function patchCachedWinner(nomineeEmail: string, claps: number, clappedByMe: boolean) {
  if (!cache) return;
  cache.data = {
    ...cache.data,
    winners: cache.data.winners.map((w) => (w.nomineeEmail === nomineeEmail ? { ...w, claps, clappedByMe } : w)),
  };
}

/** Moments for the signed-in user; null while loading or when the call fails (the sections then hide). */
export function useHubMoments(email: string): HubMomentsResponse | null {
  const fresh = cache && cache.email === email && Date.now() - cache.at < TTL_MS ? cache.data : null;
  const [data, setData] = useState<HubMomentsResponse | null>(fresh);
  useEffect(() => {
    if (!email || fresh) return;
    let alive = true;
    callHubMoments()
      .then((r) => {
        cache = { email, at: Date.now(), data: r.data };
        if (alive) setData(r.data);
      })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [email]);
  return data;
}
